import { TextInput } from '@gettactic/components/src/form/TextInput';
import { Palette, Pipette } from 'lucide-react';
import React from 'react';
import { HexColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '@gettactic/components';
import { logError } from '../../../../helpers';

export interface ColorPickerInputProps {
  value: string;
  onChange: (value: string) => void;
  showInput?: boolean;
  paletteColors?: string[];
  trigger?: React.ReactNode;
  triggerAsChild?: boolean;
}

const classes = {
  Wrapper: 'flex gap-2 items-center',
  ColorPickerPreview: 'rounded w-10 h-10 border border-gray-300',
  PickerButton: 'flex items-center gap-2',
  PickerButtonIcon: 'text-gray-600',
  PickerPopoverContent: 'flex w-auto flex-col items-center p-0',
  PaletteColorsWrapper:
    'mt-2 flex w-full max-w-[300px] items-start border-t border-t-1 bg-accent p-2',
  PaletteColorsWrapperColors: 'flex flex-1 flex-wrap gap-2',
  PaletteColorItem: 'h-5 w-5 border border-input'
};

export const ColorPickerInput: React.FC<ColorPickerInputProps> = ({
  value,
  onChange,
  showInput = true,
  paletteColors,
  trigger,
  triggerAsChild = true
}) => {
  const handleChangeColor = (color: string) => {
    onChange(color);
  };

  const colors = paletteColors ? paletteColors : [];

  const eyeDropperAllowed = 'EyeDropper' in window;

  const pickColor = async () => {
    if (eyeDropperAllowed) {
      const eyeDropper = new (window as any).EyeDropper();
      try {
        const result = await eyeDropper.open();
        if (result.sRGBHex) {
          onChange?.(result.sRGBHex);
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      logError('EyeDropper API not supported!');
    }
  };

  return (
    <div className={classes.Wrapper}>
      <Popover>
        <PopoverTrigger asChild={triggerAsChild}>
          {trigger || (
            <button className={classes.PickerButton}>
              <div
                className={classes.ColorPickerPreview}
                style={{ backgroundColor: value }}
              />
              <Palette className={classes.PickerButtonIcon} />
            </button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className={classes.PickerPopoverContent}
          align="start"
          side="top"
        >
          <HexColorPicker color={value} onChange={handleChangeColor} />
          {(colors.length > 0 || eyeDropperAllowed) && (
            <div className={classes.PaletteColorsWrapper}>
              <div className={classes.PaletteColorsWrapperColors}>
                {colors.map((color) => (
                  <button
                    key={color}
                    className={classes.PaletteColorItem}
                    style={{ backgroundColor: color }}
                    onClick={() => handleChangeColor(color)}
                  />
                ))}
              </div>
              <div>
                <div className={'w-full text-center'}>
                  <button onClick={pickColor}>
                    <Pipette className={'size-4'} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {showInput && (
        <TextInput
          value={value}
          onChange={(e) => handleChangeColor(e.target.value)}
        />
      )}
    </div>
  );
};
